import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import {
  ConjointAttributeArgsInput,
  ResponseOptionType,
  TextHighlighterCategoryArgsInput,
  UpdateAudienceMemberV2Input,
  UpdateCustomCriterionOptionV2Input,
  UpdateMatrixRowV2Input,
  UpdateQuestionnaireEntryV2Input,
  UpdateResponseOptionV2Input
} from '../../../data/gql-gen/questionnaire/graphql'
import { UPDATE_CUSTOM_CRITERION_OPTION_V2 } from '../../../data/gql/questionnaire/mutations/criterionOptionV2'
import { UPDATE_AUDIENCE_MEMBER_V2 } from '../../../data/gql/questionnaire/mutations/updateAudienceMemberV2'
import { UPDATE_MATRIX_ROW } from '../../../data/gql/questionnaire/mutations/updateMatrixRow'
import { UPDATE_QUESTIONNAIRE_ENTRY } from '../../../data/gql/questionnaire/mutations/updateQuestionnaireEntry'
import { UPDATE_RESPONSE_OPTION } from '../../../data/gql/questionnaire/mutations/updateResponseOption'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import { useSurveyId } from '../../../hooks/useSurveyId'
import { buildPlainStyling } from '../lib/previewOverlay'

export type FieldKind =
  | 'questionText'
  | 'responseOptionValue'
  | 'matrixRowText'
  | 'matrixTitle'
  | 'textCardTitle'
  | 'textCardSubtitle'
  | 'textCardBody'
  | 'scaleLabel'
  | 'maxDiffMostLabel'
  | 'maxDiffLeastLabel'
  | 'maxDiffAttributeLabel'
  | 'highlighterQuestionText'
  | 'highlighterContent'
  | 'highlighterCategory'
  | 'conjointText'
  | 'conjointAttributeName'
  | 'conjointAttributeOption'
  | 'audienceQuestionText'
  | 'audienceOptionValue'

// Every save below sends `buildPlainStyling(text)` alongside the text. Renderers
// draw a title from its Draft.js blob in preference to the plain text, and the
// backend's translation upsert inherits the MASTER blob when we omit one — so a
// translation saved without a blob renders in the master language. The master's
// inline formatting is deliberately dropped: its style ranges are anchored to the
// master text's character offsets. `buildTranslationImportEntries` does the same
// for the bulk import path.
export const useSaveTranslation = () => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()

  const [updateEntry] = useMutation(UPDATE_QUESTIONNAIRE_ENTRY, {
    context: { clientName: 'questionnaire' }
  })
  const [updateResponseOption] = useMutation(UPDATE_RESPONSE_OPTION, {
    context: { clientName: 'questionnaire' }
  })
  const [updateMatrixRow] = useMutation(UPDATE_MATRIX_ROW, {
    context: { clientName: 'questionnaire' }
  })
  const [updateAudienceMember] = useMutation(UPDATE_AUDIENCE_MEMBER_V2, {
    context: { clientName: 'questionnaire' }
  })
  const [updateCustomCriterionOption] = useMutation(
    UPDATE_CUSTOM_CRITERION_OPTION_V2,
    { context: { clientName: 'questionnaire' } }
  )

  const saveQuestionText = useCallback(
    (entryId: string, text: string, languageCode: string) => {
      const input: UpdateQuestionnaireEntryV2Input = {
        projectId,
        surveyId,
        waveId,
        entryId,
        languageCode,
        updateBasicQuestion: { text, textStyling: buildPlainStyling(text) }
      }
      return updateEntry({ variables: { input } })
    },
    [projectId, surveyId, waveId, updateEntry]
  )

  const saveResponseOptionValue = useCallback(
    (
      entryId: string,
      responseOptionId: string,
      responseOptionValue: string,
      languageCode: string
    ) => {
      const input: UpdateResponseOptionV2Input = {
        projectId,
        surveyId,
        waveId,
        entryId,
        responseOptionId,
        languageCode,
        responseOptionValue
      }
      return updateResponseOption({ variables: { input } })
    },
    [projectId, surveyId, waveId, updateResponseOption]
  )

  const saveMatrixRowText = useCallback(
    (
      entryId: string,
      questionId: string,
      text: string,
      languageCode: string
    ) => {
      const input: UpdateMatrixRowV2Input = {
        projectId,
        surveyId,
        waveId,
        entryId,
        questionId,
        languageCode,
        text,
        textStyling: buildPlainStyling(text)
      }
      return updateMatrixRow({ variables: { input } })
    },
    [projectId, surveyId, waveId, updateMatrixRow]
  )

  const saveMatrixTitle = useCallback(
    (entryId: string, title: string, languageCode: string) => {
      const input: UpdateQuestionnaireEntryV2Input = {
        projectId,
        surveyId,
        waveId,
        entryId,
        languageCode,
        updateMatrixQuestion: { title, titleStyling: buildPlainStyling(title) }
      }
      return updateEntry({ variables: { input } })
    },
    [projectId, surveyId, waveId, updateEntry]
  )

  const saveTextCardField = useCallback(
    (
      entryId: string,
      patch: { title?: string; subtitle?: string; body?: string },
      languageCode: string
    ) => {
      const input: UpdateQuestionnaireEntryV2Input = {
        projectId,
        surveyId,
        waveId,
        entryId,
        languageCode,
        // Only the title has a styling blob; subtitle and body are plain text.
        updateTextCard:
          patch.title === undefined
            ? patch
            : { ...patch, titleStyling: buildPlainStyling(patch.title) }
      }
      return updateEntry({ variables: { input } })
    },
    [projectId, surveyId, waveId, updateEntry]
  )

  // The three labels share one record per language and merge, so each saves
  // independently. None carries a styling blob.
  const saveMaxDiffLabel = useCallback(
    (
      entryId: string,
      patch: {
        mostLabel?: string
        leastLabel?: string
        attributeLabel?: string
      },
      languageCode: string
    ) => {
      const input: UpdateQuestionnaireEntryV2Input = {
        projectId,
        surveyId,
        waveId,
        entryId,
        languageCode,
        updateBasicQuestion: { maxDiffSpecification: patch }
      }
      return updateEntry({ variables: { input } })
    },
    [projectId, surveyId, waveId, updateEntry]
  )

  // Only the positions sent are upserted, so one label at a time is safe —
  // unlike the master path, where `labels` replaces the whole set. `range` is
  // omitted: not translatable. A blank value clears the translation.
  const saveScaleLabel = useCallback(
    (
      entryId: string,
      position: number,
      labelText: string,
      languageCode: string
    ) => {
      const input: UpdateQuestionnaireEntryV2Input = {
        projectId,
        surveyId,
        waveId,
        entryId,
        languageCode,
        updateBasicQuestion: {
          questionScale: { value: { labels: [{ labelText, position }] } }
        }
      }
      return updateEntry({ variables: { input } })
    },
    [projectId, surveyId, waveId, updateEntry]
  )

  const saveHighlighterText = useCallback(
    (
      entryId: string,
      patch: { questionText?: string; contentText?: string },
      languageCode: string
    ) => {
      const input: UpdateQuestionnaireEntryV2Input = {
        projectId,
        surveyId,
        waveId,
        entryId,
        languageCode,
        updateTextHighlighter: {
          ...patch,
          ...(patch.questionText === undefined
            ? {}
            : { questionTextStyling: buildPlainStyling(patch.questionText) }),
          ...(patch.contentText === undefined
            ? {}
            : { contentTextStyling: buildPlainStyling(patch.contentText) })
        }
      }
      return updateEntry({ variables: { input } })
    },
    [projectId, surveyId, waveId, updateEntry]
  )

  // Categories are not `responseOptions`, so they save through the highlighter.
  // Merged by responseOptionId; colour/position are required but unused here.
  const saveHighlighterCategory = useCallback(
    (
      entryId: string,
      category: TextHighlighterCategoryArgsInput,
      languageCode: string
    ) => {
      const input: UpdateQuestionnaireEntryV2Input = {
        projectId,
        surveyId,
        waveId,
        entryId,
        languageCode,
        updateTextHighlighter: { categories: [category] }
      }
      return updateEntry({ variables: { input } })
    },
    [projectId, surveyId, waveId, updateEntry]
  )

  const saveConjointText = useCallback(
    (entryId: string, text: string, languageCode: string) => {
      const input: UpdateQuestionnaireEntryV2Input = {
        projectId,
        surveyId,
        waveId,
        entryId,
        languageCode,
        updateConjoint: { text, textStyling: buildPlainStyling(text) }
      }
      return updateEntry({ variables: { input } })
    },
    [projectId, surveyId, waveId, updateEntry]
  )

  // One attribute at a time. `name` is non-null and written verbatim, so a
  // level edit must resend it. Levels merge by id.
  const saveConjointAttribute = useCallback(
    (
      entryId: string,
      attribute: ConjointAttributeArgsInput,
      languageCode: string
    ) => {
      const input: UpdateQuestionnaireEntryV2Input = {
        projectId,
        surveyId,
        waveId,
        entryId,
        languageCode,
        updateConjoint: { attributes: [attribute] }
      }
      return updateEntry({ variables: { input } })
    },
    [projectId, surveyId, waveId, updateEntry]
  )

  const saveAudienceQuestionText = useCallback(
    (memberId: string, text: string, languageCode: string) => {
      const input: UpdateAudienceMemberV2Input = {
        projectId,
        surveyId,
        waveId,
        memberId,
        languageCode,
        updateCustomMember: { text }
      }
      return updateAudienceMember({ variables: { input } })
    },
    [projectId, surveyId, waveId, updateAudienceMember]
  )

  const saveAudienceOptionValue = useCallback(
    (
      questionId: string,
      responseOptionId: string,
      responseOptionValue: string,
      languageCode: string
    ) => {
      const input: UpdateCustomCriterionOptionV2Input = {
        projectId,
        surveyId,
        waveId,
        questionId,
        responseOptionId,
        languageCode,
        responseOption: {
          responseOptionId,
          responseOptionType: ResponseOptionType.RoString,
          responseOptionValue
        }
      }
      return updateCustomCriterionOption({ variables: { input } })
    },
    [projectId, surveyId, waveId, updateCustomCriterionOption]
  )

  return {
    saveQuestionText,
    saveResponseOptionValue,
    saveMatrixRowText,
    saveMatrixTitle,
    saveTextCardField,
    saveMaxDiffLabel,
    saveScaleLabel,
    saveHighlighterText,
    saveHighlighterCategory,
    saveConjointText,
    saveConjointAttribute,
    saveAudienceQuestionText,
    saveAudienceOptionValue
  }
}
